<!--
  SILL-1533 (CUEST02.2) — Vista pública del cuestionario de falla del equipo,
  con el diseño de CUEST02.1 (SILL-1532) aplicado.

  El cliente llega acá desde el link del correo, sin login: la ruta es pública y
  lo único que la protege es el token UUID del formulario.

  El chrome (header con los logos + "Salir", el fondo con la marca de agua y el
  footer) reusa los mismos tokens que las vistas públicas de Garantías
  (`OVGStore`), que ya venían de este mismo sistema de diseño.
-->
<template>
  <div class="ff-page">
    <div class="ff-page__bg" aria-hidden="true" />

    <header class="ff-header">
      <div class="ff-header__logos">
        <img
          src="@/assets/logo_original.svg"
          alt="Samtek"
          class="ff-header__logo-samtek"
        />
        <img
          src="@/assets/logo_asus.png"
          alt="ASUS"
          class="ff-header__logo-asus"
        />
      </div>
      <button type="button" class="ff-header__salir" @click="salir">
        Salir <font-awesome-icon icon="sign-out-alt" />
      </button>
    </header>

    <main class="ff-main">
      <h1 class="ff-titulo">Cuestionario de falla computador ASUS</h1>
      <p v-if="otNumero" class="ff-ot">OT: {{ otNumero }}</p>

      <!-- Cargando -->
      <section v-if="loading" class="ff-card ff-card--centrada">
        <b-spinner label="Cargando" />
        <p class="ff-card__texto">Cargando el cuestionario…</p>
      </section>

      <!-- Link inválido -->
      <section v-else-if="errorCarga" class="ff-card ff-card--centrada">
        <h2 class="ff-card__titulo">No pudimos abrir el cuestionario</h2>
        <p class="ff-card__texto">{{ errorCarga }}</p>
      </section>

      <!-- Recién enviado -->
      <section v-else-if="recienEnviado" class="ff-card ff-card--centrada">
        <h2 class="ff-card__titulo">Respuestas Recibidas</h2>
        <font-awesome-icon icon="check-circle" class="ff-check" />
        <p class="ff-card__texto">
          Ya hemos registrado la información técnica para la
          <strong>Orden #{{ otNumero }}</strong>
        </p>
        <p class="ff-card__texto">
          Nuestro equipo está utilizando estos datos para el diagnostico de tu
          equipo
        </p>
        <p class="ff-card__texto">Ya puedes cerrar esta ventana.</p>
      </section>

      <!-- Ya respondido: el cliente vuelve a abrir el link del correo -->
      <section v-else-if="completado" class="ff-card ff-card--centrada">
        <h2 class="ff-card__titulo">Cuestionario ya completado</h2>
        <font-awesome-icon icon="check-circle" class="ff-check" />
        <p class="ff-card__texto">
          Recibimos tus respuestas correctamente y ya están adjuntas a la
          <strong>Orden #{{ otNumero }}</strong
          >.
        </p>
        <p class="ff-card__texto">
          Nuestro equipo técnico cuenta con toda esta información para el
          diagnóstico, por lo que no es necesario volver a enviarla.
        </p>
        <p class="ff-card__texto">Ya puedes cerrar esta ventana.</p>
      </section>

      <!-- Sin preguntas configuradas: el sistema no debe permitir este estado,
           pero antes de la carga inicial desde el admin no hay ninguna. -->
      <section v-else-if="!preguntas.length" class="ff-card ff-card--centrada">
        <h2 class="ff-card__titulo">El cuestionario no está disponible</h2>
        <p class="ff-card__texto">
          Vuelve a intentarlo más tarde. Si el problema persiste, responde el
          correo que recibiste y te ayudamos.
        </p>
      </section>

      <!-- Cuestionario -->
      <form v-else class="ff-card" @submit.prevent="enviar">
        <p class="ff-intro">
          Para proporcionar al técnico una mejor comprensión de su caso, ponemos
          a vuestra disposición el siguiente formulario. Esta información le
          brindará al técnico mayor contexto de la falla para poder evaluar el
          equipo
        </p>

        <ol class="ff-preguntas">
          <li
            v-for="(pregunta, indice) in preguntas"
            :key="pregunta.id"
            class="ff-pregunta"
          >
            <span class="ff-pregunta__numero">{{ indice + 1 }}.</span>
            <div class="ff-pregunta__cuerpo">
              <p class="ff-pregunta__texto">{{ pregunta.pregunta }}</p>

              <!-- Texto libre -->
              <textarea
                v-if="pregunta.tipo === 'TEXTO' && pregunta.multilinea"
                v-model="respuestas[pregunta.id]"
                class="ff-input ff-input--area"
                rows="3"
                :maxlength="MAX_LARGO_TEXTO"
                :placeholder="placeholderDe(pregunta)"
              />
              <input
                v-else-if="pregunta.tipo === 'TEXTO'"
                v-model="respuestas[pregunta.id]"
                type="text"
                class="ff-input"
                :maxlength="MAX_LARGO_TEXTO"
                :placeholder="placeholderDe(pregunta)"
              />

              <!-- Selección múltiple -->
              <template v-else-if="pregunta.tipo === 'CHECK'">
                <label
                  v-for="opcion in pregunta.opciones"
                  :key="opcion"
                  class="ff-opcion"
                >
                  <input
                    v-model="respuestas[pregunta.id]"
                    type="checkbox"
                    :value="opcion"
                    class="ff-opcion__input"
                  />
                  <span class="ff-opcion__caja" aria-hidden="true" />
                  <span class="ff-opcion__texto">{{ opcion }}</span>
                </label>
                <div v-if="pregunta.permite_otra" class="ff-opcion">
                  <label class="ff-opcion__control">
                    <input
                      v-model="otraMarcada[pregunta.id]"
                      type="checkbox"
                      class="ff-opcion__input"
                    />
                    <span class="ff-opcion__caja" aria-hidden="true" />
                  </label>
                  <input
                    v-model="otraTexto[pregunta.id]"
                    type="text"
                    class="ff-input ff-input--otra"
                    :maxlength="MAX_LARGO_TEXTO"
                    placeholder="Otra"
                    aria-label="Otra"
                    @focus="marcarOtra(pregunta)"
                  />
                </div>
              </template>

              <!-- Selección única -->
              <template v-else-if="pregunta.tipo === 'RADIO'">
                <label
                  v-for="opcion in pregunta.opciones"
                  :key="opcion"
                  class="ff-opcion"
                >
                  <input
                    v-model="respuestas[pregunta.id]"
                    type="radio"
                    :value="opcion"
                    :name="`pregunta-${pregunta.id}`"
                    class="ff-opcion__input"
                  />
                  <span class="ff-opcion__circulo" aria-hidden="true" />
                  <span class="ff-opcion__texto">{{ opcion }}</span>
                </label>
                <div v-if="pregunta.permite_otra" class="ff-opcion">
                  <label class="ff-opcion__control">
                    <input
                      v-model="respuestas[pregunta.id]"
                      type="radio"
                      :value="VALOR_OTRA"
                      :name="`pregunta-${pregunta.id}`"
                      class="ff-opcion__input"
                    />
                    <span class="ff-opcion__circulo" aria-hidden="true" />
                  </label>
                  <input
                    v-model="otraTexto[pregunta.id]"
                    type="text"
                    class="ff-input ff-input--otra"
                    :maxlength="MAX_LARGO_TEXTO"
                    placeholder="Otra"
                    aria-label="Otra"
                    @focus="marcarOtra(pregunta)"
                  />
                </div>
              </template>
            </div>
          </li>
        </ol>

        <p v-if="errorEnvio" class="ff-error">{{ errorEnvio }}</p>

        <button type="submit" class="ff-submit" :disabled="enviando">
          <b-spinner v-if="enviando" small class="mr-2" />
          Enviar Respuestas
        </button>
      </form>
    </main>

    <footer class="ff-footer">
      <p class="ff-footer__linea">© 2026 Samtek</p>
      <p class="ff-footer__linea">
        Servicio Técnico ASUS DELL Autorizado en Chile.
      </p>
    </footer>
  </div>
</template>

<script>
import {
  obtenerFormularioPublico,
  responderFormulario,
} from '@/services/formularioFallaService'

// Valor centinela de la opción "Otra" en las preguntas de selección única: el
// texto que escribe el cliente vive aparte, en `otraTexto`.
const VALOR_OTRA = '__OTRA__'

// Mismo tope que valida el backend (orden.formulario_falla).
const MAX_LARGO_TEXTO = 2000

export default {
  name: 'FormularioFallaView',
  data() {
    return {
      VALOR_OTRA,
      MAX_LARGO_TEXTO,
      loading: true,
      enviando: false,
      errorCarga: null,
      errorEnvio: null,
      completado: false,
      recienEnviado: false,
      otNumero: null,
      preguntas: [],
      // { [preguntaId]: string | string[] }
      respuestas: {},
      // Opción "Otra": si está marcada (sólo CHECK) y qué escribió el cliente.
      otraMarcada: {},
      otraTexto: {},
    }
  },
  computed: {
    token() {
      return this.$route.params.token
    },
    /** El backend exige respuesta para todas las preguntas activas. */
    faltantes() {
      return this.preguntas.filter((pregunta) => !this.tieneRespuesta(pregunta))
    },
  },
  watch: {
    // El aviso de "quedan N sin responder" no debe quedar colgado en pantalla
    // una vez que el cliente completa lo que faltaba.
    faltantes(pendientes) {
      if (!pendientes.length) this.errorEnvio = null
    },
  },
  created() {
    this.cargar()
  },
  methods: {
    placeholderDe(pregunta) {
      return pregunta.placeholder || 'Escriba su respuesta'
    },

    /** Escribir en el campo "Otra" equivale a elegir esa opción. */
    marcarOtra(pregunta) {
      if (pregunta.tipo === 'CHECK') {
        this.$set(this.otraMarcada, pregunta.id, true)
      } else {
        this.$set(this.respuestas, pregunta.id, VALOR_OTRA)
      }
    },

    tieneRespuesta(pregunta) {
      const valor = this.respuestas[pregunta.id]
      const otra = (this.otraTexto[pregunta.id] || '').trim()

      if (pregunta.tipo === 'CHECK') {
        const marcadas = Array.isArray(valor) ? valor.length : 0
        if (this.otraMarcada[pregunta.id]) return Boolean(otra)
        return marcadas > 0
      }

      if (pregunta.tipo === 'RADIO' && valor === VALOR_OTRA) {
        return Boolean(otra)
      }

      return Boolean(valor && String(valor).trim())
    },

    /** Respuesta final de una pregunta, ya con la opción "Otra" resuelta. */
    valorDe(pregunta) {
      const valor = this.respuestas[pregunta.id]
      const otra = (this.otraTexto[pregunta.id] || '').trim()

      if (pregunta.tipo === 'CHECK') {
        const marcadas = Array.isArray(valor) ? valor.slice() : []
        if (this.otraMarcada[pregunta.id] && otra) marcadas.push(otra)
        return marcadas
      }

      if (pregunta.tipo === 'RADIO' && valor === VALOR_OTRA) return otra

      return valor
    },

    salir() {
      // La ventana suele venir de un click en el correo, así que cerrarla es lo
      // que corresponde ("Ya puedes cerrar esta ventana"). Si el navegador no lo
      // permite (la pestaña no la abrió un script), se va al sitio de Samtek.
      window.close()
      window.location.href = 'https://www.samtek.cl'
    },

    cargar() {
      this.loading = true
      this.errorCarga = null
      return obtenerFormularioPublico(this.token)
        .then((data) => {
          this.aplicarDatos(data)
        })
        .catch((error) => {
          const status = error && error.response ? error.response.status : null
          this.errorCarga =
            status === 404
              ? 'El link del cuestionario no es válido.'
              : 'Ocurrió un problema al cargar el cuestionario. Intenta de nuevo más tarde.'
        })
        .finally(() => {
          this.loading = false
        })
    },

    aplicarDatos(data) {
      this.otNumero = data.ot
      this.completado = data.completado
      this.preguntas = data.preguntas || []
      this.respuestas = this.preguntas.reduce((acc, pregunta) => {
        acc[pregunta.id] = pregunta.tipo === 'CHECK' ? [] : ''
        return acc
      }, {})
      this.otraMarcada = this.preguntas.reduce((acc, pregunta) => {
        acc[pregunta.id] = false
        return acc
      }, {})
      this.otraTexto = this.preguntas.reduce((acc, pregunta) => {
        acc[pregunta.id] = ''
        return acc
      }, {})
    },

    enviar() {
      // El diseño no contempla un botón deshabilitado: se valida al enviar y
      // se avisa en pantalla, sin gastar una vuelta al servidor.
      const faltan = this.faltantes.length
      if (faltan) {
        this.errorEnvio =
          faltan === 1
            ? 'Queda 1 pregunta sin responder.'
            : `Quedan ${faltan} preguntas sin responder.`
        return Promise.resolve()
      }

      this.enviando = true
      this.errorEnvio = null
      const payload = this.preguntas.map((pregunta) => ({
        pregunta: pregunta.id,
        valor: this.valorDe(pregunta),
      }))

      return responderFormulario(this.token, payload)
        .then(() => {
          this.recienEnviado = true
          this.completado = true
          this.preguntas = []
        })
        .catch((error) => {
          const status = error && error.response ? error.response.status : null
          if (status === 409) {
            // Otro envío del mismo link llegó primero.
            this.completado = true
            this.preguntas = []
            return
          }
          this.errorEnvio =
            status === 400
              ? 'Revisa las respuestas: quedaron preguntas sin responder o con una opción no válida.'
              : 'No pudimos enviar tus respuestas. Intenta de nuevo en unos minutos.'
        })
        .finally(() => {
          this.enviando = false
        })
    },
  },
}
</script>

<style scoped>
/* Tokens del diseño de CUEST02.1. El azul de marca está acá arriba a propósito:
   es el único valor que hay que retocar si el Figma define otro exacto. */
.ff-page {
  /* Títulos, subtítulos y texto: #23201C. El azul queda sólo para el fondo del
     botón de envío, que es lo único que el diseño pinta de ese color. */
  --ff-texto: #23201c;
  --ff-navy: #183a63;
  --ff-texto-suave: #525252;
  --ff-borde: #23201c;
  --ff-verde: #22c55e;

  font-family: 'Noto Sans', sans-serif;
  min-height: 100vh;
  background-color: #f5f6fa;
  display: flex;
  flex-direction: column;
  position: relative;
}

.ff-page__bg {
  /* Sin `opacity`: el PNG ya es una marca de agua (su patrón viene con
     alpha 13/255). Desvanecerlo otra vez acá lo dejaba en 0,62% de
     contraste, o sea invisible en pantalla. */
  position: fixed;
  inset: 0;
  background-image: url('~@/assets/fondo_samtek.png');
  background-size: cover;
  background-position: center;
  pointer-events: none;
  z-index: 0;
}

/* ---------------------------------------------------------------- Header */
.ff-header {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 55px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 162px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.ff-header__logos {
  display: flex;
  align-items: center;
  gap: 15px;
}

.ff-header__logo-samtek,
.ff-header__logo-asus {
  height: 18px;
  width: auto;
}

.ff-header__salir {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: var(--ff-texto);
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

/* ----------------------------------------------------------------- Cuerpo */
.ff-main {
  position: relative;
  z-index: 1;
  flex: 1 0 auto;
  width: 100%;
  max-width: 740px;
  margin: 0 auto;
  padding: 24px 24px 32px;
  box-sizing: border-box;
}

.ff-titulo {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--ff-texto);
  margin: 0 0 6px;
}

.ff-ot {
  font-size: 16px;
  font-weight: 700;
  color: var(--ff-texto);
  margin: 0 0 20px;
}

.ff-card {
  background: #ffffff;
  border-radius: 10px;
  padding: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
}

.ff-card--centrada {
  align-items: center;
  text-align: center;
  gap: 16px;
}

.ff-card--centrada .ff-card__titulo {
  align-self: flex-start;
  text-align: left;
}

.ff-card__titulo {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--ff-texto);
  margin: 0;
}

.ff-card__texto {
  font-size: 14px;
  color: var(--ff-texto-suave);
  line-height: 1.5;
  margin: 0;
  max-width: 420px;
}

.ff-check {
  font-size: 56px;
  color: var(--ff-verde);
}

.ff-intro {
  font-size: 14px;
  color: var(--ff-texto-suave);
  line-height: 1.5;
  margin: 0 0 24px;
}

/* -------------------------------------------------------------- Preguntas */
.ff-preguntas {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ff-pregunta {
  display: flex;
  gap: 8px;
  margin-bottom: 26px;
}

.ff-pregunta__numero {
  font-size: 15px;
  font-weight: 700;
  color: var(--ff-texto);
  line-height: 1.35;
  flex-shrink: 0;
}

.ff-pregunta__cuerpo {
  flex: 1;
  min-width: 0;
}

.ff-pregunta__texto {
  font-size: 15px;
  font-weight: 700;
  color: var(--ff-texto);
  line-height: 1.35;
  margin: 0 0 12px;
}

.ff-input {
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  padding: 10px 14px;
  border: 1px solid #c9ccd1;
  border-radius: 8px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  color: var(--ff-texto);
  background: #ffffff;
}

.ff-input::placeholder {
  color: #9aa0a6;
}

.ff-input:focus {
  outline: none;
  border-color: var(--ff-navy);
}

.ff-input--area {
  height: auto;
  min-height: 86px;
  resize: vertical;
}

.ff-input--otra {
  height: 34px;
  padding: 6px 12px;
}

/* Casillas y círculos dibujados a mano: los nativos no se pueden llevar al
   grosor de borde del diseño de forma consistente entre navegadores. */
.ff-opcion {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 12px;
  cursor: pointer;
}

.ff-opcion:last-child {
  margin-bottom: 0;
}

/* La fila "Otra" no puede envolver el campo de texto en el <label> del control:
   al hacer clic en el texto, el navegador reenvía la activación al checkbox y lo
   destilda. El label envuelve sólo la casilla. */
.ff-opcion__control {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0;
}

.ff-opcion__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.ff-opcion__caja,
.ff-opcion__circulo {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 2px solid var(--ff-borde);
  background: #ffffff;
  position: relative;
}

.ff-opcion__caja {
  border-radius: 3px;
}

.ff-opcion__circulo {
  border-radius: 50%;
}

.ff-opcion__input:checked + .ff-opcion__caja::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 0;
  width: 7px;
  height: 12px;
  border: solid var(--ff-borde);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.ff-opcion__input:checked + .ff-opcion__circulo::after {
  content: '';
  position: absolute;
  top: 3px;
  right: 3px;
  bottom: 3px;
  left: 3px;
  border-radius: 50%;
  background: var(--ff-borde);
}

.ff-opcion__texto {
  font-size: 14px;
  color: var(--ff-texto);
  line-height: 1.35;
}

/* ---------------------------------------------------------------- Acciones */
.ff-error {
  font-size: 14px;
  color: #c0392b;
  margin: 0 0 12px;
}

.ff-submit {
  width: 100%;
  height: 46px;
  background: var(--ff-navy);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
}

.ff-submit:hover:not(:disabled) {
  opacity: 0.9;
}

.ff-submit:disabled {
  opacity: 0.7;
  cursor: progress;
}

/* ---------------------------------------------------------------- Footer */
.ff-footer {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  padding: 16px 20px 20px;
  text-align: center;
}

.ff-footer__linea {
  font-size: 14px;
  color: var(--ff-texto-suave);
  margin: 0;
  line-height: 1.5;
}

/* ============================================================
   MOBILE — max-width: 767px
   ============================================================ */
@media (max-width: 767px) {
  .ff-header {
    padding: 0 20px;
  }

  .ff-main {
    padding: 20px 24px 28px;
  }

  .ff-titulo {
    font-size: 23px;
  }

  .ff-card {
    padding: 20px;
  }
}
</style>
